// src/pages/admin/VendorManagement/Applications.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, Eye, Building2, Mail, Phone, Calendar,
  Users, Clock, CheckCircle, XCircle, AlertCircle, ChevronLeft, ChevronRight,
  FileText
} from 'lucide-react';
import { adminAPI } from '../../../../api/admin';
import ReviewApplicationModal from '../../../../components/admin/ReviewApplicationModal';
import { useDebounce } from '../../../../hooks/useDebounce';
import ReviewButton from '../../../../components/admin/ReviewButton';

interface Application {
  id: number;
  business_name: string;
  business_email: string;
  business_phone: string;
  applicant_name: string;
  vendor_type: string;
  verification_status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
}

const Applications: React.FC = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const debouncedSearch = useDebounce(search, 500);

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    pages: 0,
    pageSize: 20,
  });

  useEffect(() => {
    fetchApplications();
  }, [debouncedSearch, filter, pagination.page]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        page_size: pagination.pageSize,
      };

      if (filter !== 'all') {
        params.status = filter;
      }
      if (search.trim()) {
        params.search = search.trim();
      }

      const response = await adminAPI.getVendorApplications(params);

      // Handle different response structures
      const data = response.results;

      setApplications(Array.isArray(data) ? data : []);
      setPagination({
        page: data.current_page || pagination.page,
        total: data.count || data.length || 0,
        pages: data.total_pages || Math.ceil((data.count || data.length || 0) / pagination.pageSize),
        pageSize: pagination.pageSize,
      });
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getVerificationBadge = (status: string) => {
    switch (status) {
      case 'approved': return { label: 'Approved', className: 'bg-green-100 text-green-700' };
      case 'pending': return { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' };
      case 'rejected': return { label: 'Rejected', className: 'bg-red-100 text-red-700' };
      default: return { label: 'Unknown', className: 'bg-gray-100 text-gray-500' };
    }
  };

  const getVerificationIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="h-3.5 w-3.5" />;
      case 'pending': return <Clock className="h-3.5 w-3.5" />;
      case 'rejected': return <XCircle className="h-3.5 w-3.5" />;
      default: return null;
    }
  };

  const handleReviewClick = (app: Application) => {
    setSelectedApplication(app);
    setShowReviewModal(true);
  };

  const handleReviewComplete = async (action: 'approve' | 'reject', reason?: string) => {
    if (!selectedApplication) return;

    setIsProcessing(true);
    try {
      if (action === 'approve') {
        await adminAPI.approveVendorApplication(selectedApplication.id);
      } else {
        await adminAPI.rejectVendorApplication(selectedApplication.id, reason || '');
      }
      await fetchApplications();
      setShowReviewModal(false);
      setSelectedApplication(null);
    } catch (error) {
      console.error('Failed to review application:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-sm text-gray-500">Review and manage vendor applications</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            <span className="font-medium text-amber-600">
              {applications.filter(a => a.verification_status === 'pending').length}
            </span> pending
          </span>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by business name, applicant name, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Filter className="h-4 w-4" />
          {filter !== 'all' && (
            <span className="w-2 h-2 bg-blue-600 rounded-full" />
          )}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
          {filterOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                setFilter(opt.value);
                setShowFilters(false);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === opt.value
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No applications found</p>
          <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Business</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Applicant</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Contact</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Submitted</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {applications.map((app) => {
                    const status = getVerificationBadge(app.verification_status);
                    const statusIcon = getVerificationIcon(app.verification_status);

                    return (
                      <tr
                        key={app.id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        // onClick={() => navigate(`/dashboard/vendor-applications/${app.id}`)}
                        onClick={() => handleReviewClick(app)}
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{app.business_name}</p>
                            <p className="text-xs text-gray-500">{app.business_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <Users className="h-3.5 w-3.5 text-gray-400" />
                            <span className="text-sm text-gray-700">
                              {app.applicant_name || app.user?.first_name + ' ' + app.user?.last_name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-600">
                            <p>{app.business_email}</p>
                            <p className="text-xs text-gray-400">{app.business_phone || 'N/A'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.className}`}>
                            {statusIcon}
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatDate(app.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <ReviewButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReviewClick(app);
                              }}
                              label="Review"
                              variant="primary"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Results count */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
              Showing {applications.length} application{applications.length !== 1 ? 's' : ''}
              {search ? ` matching "${search}"` : ' '}
              of {pagination.total} total
            </div>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {applications.length} of {pagination.total} applications
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page <= 1}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.pages}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
      {showReviewModal && selectedApplication && (
        <ReviewApplicationModal
          isOpen={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedApplication(null);
          }}
          onConfirm={handleReviewComplete}
          application={selectedApplication}
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
};

export default Applications;